import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, CheckCircle, Sparkles, PartyPopper, LogIn, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RegistrationLayout } from "@/components/auth/RegistrationLayout";
import { RegistrationForm } from "@/components/auth/RegistrationForm";
import { Logger } from "@/lib/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const logger = new Logger('SpecialistRegistration');

export function SpecialistRegistration() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  const [userName, setUserName] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    businessName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  // Handle countdown and redirect
  useEffect(() => {
    if (redirectCountdown === null) return;
    
    if (redirectCountdown <= 0) {
      navigate("/coach-signin");
      return;
    }
    
    const timer = setTimeout(() => {
      setRedirectCountdown(prev => prev !== null ? prev - 1 : null);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [redirectCountdown, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formData.fullName || !formData.businessName || !formData.email || !formData.password) {
      toast({
        variant: "destructive",
        title: "All fields are required",
        description: "Please fill in all the fields to continue.",
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please ensure both passwords match.",
      });
      return false;
    }

    if (formData.password.length < 8) {
      toast({
        variant: "destructive",
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
      });
      return false;
    }

    return true;
  };

  const createSpecialistRecord = async (userId: string) => {
    try {
      logger.info('Creating specialist record', { userId });
      const { data, error } = await supabase
        .from('specialists')
        .insert([
          {
            auth_user_id: userId,
            name: formData.fullName,
            email: formData.email,
            business_name: formData.businessName,
            active: true,
            verification_status: 'pending'
          }
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error creating specialist record:', error);
      throw new Error('Failed to create specialist record');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Step 1: Create auth user
      logger.info('Creating auth user');
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            role: "specialist",
            name: formData.fullName,
            business_name: formData.businessName,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('No user data returned');

      // Step 2: Create specialist record
      await createSpecialistRecord(authData.user.id);

      // Step 3: Show success animation instead of immediate redirect
      setUserName(formData.fullName.split(' ')[0]); // Use first name for personalization
      setRegistrationSuccess(true);
      // Start countdown for 10 seconds before redirecting
      setRedirectCountdown(10);
      
      // Still show toast for confirmation
      toast({
        title: "Registration successful!",
        description: "Your account has been created.",
      });
    } catch (error: any) {
      logger.error('Registration failed:', error);
      toast({
        variant: "destructive",
        title: "Registration failed",
        description: error.message || "An unexpected error occurred",
      });
      setLoading(false);
    }
  };

  const formFields = [
    {
      id: "fullName",
      label: "Full Name",
      type: "text",
      placeholder: "Enter your full name",
      value: formData.fullName,
      onChange: handleChange,
      helperText: "Your legal name as it appears on your credentials",
    },
    {
      id: "businessName",
      label: "Business Name",
      type: "text",
      placeholder: "Enter your business name",
      value: formData.businessName,
      onChange: handleChange,
      helperText: "The name of your sleep consulting practice",
    },
    {
      id: "email",
      label: "Email Address",
      type: "email",
      placeholder: "Enter your email address",
      value: formData.email,
      onChange: handleChange,
      helperText: "We'll use this for login and communications",
    },
    {
      id: "password",
      label: "Password",
      type: "password",
      placeholder: "Create a strong password",
      value: formData.password,
      onChange: handleChange,
      helperText: "Minimum 8 characters",
    },
    {
      id: "confirmPassword",
      label: "Confirm Password",
      type: "password",
      placeholder: "Confirm your password",
      value: formData.confirmPassword,
      onChange: handleChange,
      helperText: "Re-enter your password to confirm",
    },
  ];

  if (registrationSuccess) {
    return (
      <RegistrationLayout 
        title="Account Created Successfully!"
        subtitle="Your account has been created. You'll be redirected to sign in shortly."
      >
        <div className="flex flex-col items-center justify-center space-y-6 p-6">
          <div className="bg-green-100 text-green-600 rounded-full p-5 flex items-center justify-center">
            <CheckCircle size={64} strokeWidth={1.5} />
          </div>
          
          <div className="text-center space-y-3">
            <h3 className="text-xl font-medium">Welcome to SleepNest, {userName || 'Specialist'}!</h3>
            <p className="text-muted-foreground">
              Redirecting to sign in page in {redirectCountdown} seconds...
            </p>
          </div>
          
          <Progress 
            value={(redirectCountdown as number / 10) * 100} 
            className="w-full max-w-md h-2"
          />
          
          <motion.div 
            className="absolute inset-0 pointer-events-none"
            animate={{ 
              opacity: [0, 0.8, 0],
            }}
            transition={{ 
              duration: 4, 
              repeat: 1,
              repeatDelay: 2
            }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                initial={{
                  x: `${Math.random() * 100}%`,
                  y: `${Math.random() * 100}%`,
                  opacity: 0,
                }}
                animate={{
                  y: [`${Math.random() * 100}%`, `${Math.random() * 20}%`],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 3 + Math.random() * 2,
                  delay: i * 0.3,
                  ease: "easeInOut",
                }}
              >
                <Sparkles className="h-6 w-6 text-primary/40" />
              </motion.div>
            ))}
          </motion.div>
          
          <Button
            onClick={() => navigate("/coach-signin")}
            className="flex items-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            Sign in now
          </Button>
        </div>
      </RegistrationLayout>
    );
  }

  return (
    <RegistrationLayout 
      title="Create Your Specialist Account"
      subtitle="Join SleepNest and start helping caregivers with sleep support"
    >
      <RegistrationForm
        fields={formFields}
        onSubmit={handleSubmit}
        loading={loading}
        submitText="Create Account"
        loadingText="Creating Account..."
      />
    </RegistrationLayout>
  );
}
